var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { attr } from "@microsoft/fast-element";
import { BaseButton } from "@fluentui/web-components";
import { CompoundButtonAppearances } from "./compound-button.options.js";
/**
 * CompoundButton
 * @summary The base class used for constructing a fabric-compound-button custom element, which extends the Fluent UI CompoundButton with Fabric-specific functionality.
 *
 * @example
 * ```html
 * <fabric-compound-button appearance="primary" shape="rounded" size="large" type="submit" autofocus>
 *   <span slot="start">🚀</span>
 *   Your Text Here
 *   <span slot="end">→</span>
 *   <span slot="description">Detailed description here</span>
 * </fabric-compound-button>
 * ```
 *
 * @attr {CompoundButtonAppearance | undefined} appearance - Indicates the styled appearance of the button.
 * @attr {CompoundButtonShape | undefined} shape - The shape of the button.
 * @attr {CompoundButtonSize | undefined} size - The size of the button.
 * @attr {boolean | undefined} icon-only - Indicates that the button should only display as an icon with no text content.
 * @attr {boolean | undefined} autofocus - Indicates the button should be focused when the page is loaded.
 * @attr {boolean | undefined} disabled-focusable - Indicates that the button is focusable while disabled.
 * @attr {number | undefined} tabindex - Sets that the button tabindex attribute.
 * @attr {string | undefined} formaction - The URL that processes the form submission.
 * @attr {string | undefined} form - The id of a form to associate the element to.
 * @attr {string | undefined} formenctype - The encoding type for the form submission.
 * @attr {string | undefined} formmethod - The HTTP method that the browser uses to submit the form.
 * @attr {boolean | undefined} formnovalidate - Indicates that the form will not be validated when submitted.
 * @attr {CompoundButtonFormTarget | undefined} formtarget - The target frame or window to open the form submission in.
 * @attr {string | undefined} name - The name of the element. This element's value will be surfaced during form submission under the provided name.
 * @attr {string | undefined} type - The button type.
 * @attr {string | undefined} value - The value attribute.
 *
 * @prop {CompoundButtonAppearance | undefined} appearance - Indicates the styled appearance of the button.
 * @prop {CompoundButtonShape | undefined} shape - The shape of the button.
 * @prop {CompoundButtonSize | undefined} size - The size of the button.
 * @prop {boolean | undefined} iconOnly - Indicates that the button should only display as an icon with no text content.
 * @prop {boolean | undefined} autofocus - Indicates the button should be focused when the page is loaded.
 * @prop {boolean | undefined} disabledFocusable - Indicates that the button is focusable while disabled.
 * @prop {number | undefined} tabIndex - Sets that the button tabindex attribute.
 * @prop {string | undefined} formAction - The URL that processes the form submission.
 * @prop {string | undefined} form - The id of a form to associate the element to.
 * @prop {string | undefined} formEnctype - The encoding type for the form submission.
 * @prop {string | undefined} formMethod - The HTTP method that the browser uses to submit the form.
 * @prop {boolean | undefined} formNoValidate - Indicates that the form will not be validated when submitted.
 * @prop {CompoundButtonFormTarget | undefined} formTarget - The target frame or window to open the form submission in.
 * @prop {string | undefined} name - The name of the element. This element's value will be surfaced during form submission under the provided name.
 * @prop {string | undefined} type - The button type.
 * @prop {string | undefined} value - The value attribute.
 *
 * @slot start - Content which can be provided before the button content.
 * @slot end - Content which can be provided after the button content.
 * @slot - The default slot for button content.
 * @slot description - A slot for providing additional description.
 *
 * @csspart content - The button content container.
 *
 * @method press - Presses the button.
 * @method resetForm - Resets the associated form.
 *
 * @extends FluentCompoundButton
 * @tagname fabric-compound-button
 * @public
 */
export class CompoundButton extends BaseButton {
    constructor() {
        super(...arguments);
        /**
         * The appearance of the compound button.
         *
         * @public
         * @remarks
         * HTML Attribute: `appearance`
         */
        this.appearance = CompoundButtonAppearances.primary;
        /**
         * Indicates that the compound button should only display as an icon with no text content.
         *
         * @public
         * @remarks
         * HTML Attribute: `icon-only`
         */
        this.iconOnly = false;
    }
}
__decorate([
    attr
], CompoundButton.prototype, "appearance", void 0);
__decorate([
    attr
], CompoundButton.prototype, "shape", void 0);
__decorate([
    attr
], CompoundButton.prototype, "size", void 0);
__decorate([
    attr({ attribute: "icon-only", mode: "boolean" })
], CompoundButton.prototype, "iconOnly", void 0);
//# sourceMappingURL=compound-button.js.map